import { YearStage } from '@/types/period';

export type Language = 'vi' | 'en';

export interface Labels {
  select: string;
  close: string;
  clear: string;
  year: string;
  months: string[];
}

interface Month {
  index: number;
  monthStr: string;
  displayName: string;
  quarterIndex: number;
  actualMonth: number;
  actualYear: number;
}

// Labels cho DatePickerBSC component (tái sử dụng)
export const datePickerLabels: Record<Language, Labels> = {
  vi: {
    select: 'Chọn',
    close: 'Đóng',
    clear: 'Xóa',
    year: 'Năm ',
    months: [
      'Thg 01',
      'Thg 02',
      'Thg 03',
      'Thg 04',
      'Thg 05',
      'Thg 06',
      'Thg 07',
      'Thg 08',
      'Thg 09',
      'Thg 10',
      'Thg 11',
      'Thg 12',
    ],
  },
  en: {
    select: 'Select',
    close: 'Close',
    clear: 'Clear',
    year: '',
    months: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  },
};

/**
 * Lấy thông tin năm tài chính từ yearData dựa trên year
 */
const getFiscalYearInfo = (
  year: number,
  yearData?: YearStage[]
): { isNamTaiChinh: boolean; monthTaiChinh: number } => {
  const stage = yearData?.find((s) => s.years.includes(String(year)));
  return {
    isNamTaiChinh: stage?.namTaiChinh ?? false,
    monthTaiChinh: stage?.monthTaiChinh ?? 0,
  };
};

/**
 * Tính toán mảng months dựa trên năm tài chính hoặc năm dương lịch
 */
const calculateMonths = (
  effectiveYear: number,
  isNamTaiChinh: boolean,
  monthTaiChinhValue: number,
  monthsLabels: string[]
): Month[] => {
  const months: Month[] = [];

  if (isNamTaiChinh) {
    let quarterIndex = 1;
    const monthOffset = monthTaiChinhValue - 1;
    for (let i = 1; i <= 12; i++) {
      let monthNum = i + monthOffset;
      let yearOffset = 0;
      if (monthNum > 12) {
        monthNum -= 12;
        yearOffset = 1;
      }
      months.push({
        index: i - 1,
        monthStr: monthNum < 10 ? `0${monthNum}` : `${monthNum}`,
        displayName: monthsLabels[monthNum - 1],
        quarterIndex,
        actualMonth: monthNum,
        actualYear: effectiveYear + yearOffset,
      });
      if (i % 3 === 0) quarterIndex++;
    }
  } else {
    months.push(
      ...monthsLabels.map((month, index) => ({
        index,
        monthStr: index + 1 < 10 ? `0${index + 1}` : `${index + 1}`,
        displayName: month,
        quarterIndex: Math.floor(index / 3) + 1,
        actualMonth: index + 1,
        actualYear: effectiveYear,
      }))
    );
  }

  return months;
};

/**
 * Tìm index của tháng hiện tại trong mảng months
 */
const findCurrentMonthIndex = (
  currentMonth: number,
  isNamTaiChinh: boolean,
  monthTaiChinhValue: number
): number => {
  if (isNamTaiChinh) {
    const monthOffset = monthTaiChinhValue - 1;
    for (let i = 1; i <= 12; i++) {
      let monthNum = i + monthOffset;
      if (monthNum > 12) {
        monthNum -= 12;
      }
      if (monthNum === currentMonth) {
        return i - 1;
      }
    }
    return -1;
  }
  return currentMonth - 1;
};

/**
 * Helper: Tính chuỗi dateRange cho quý hiện tại (DD/MM/YYYY - DD/MM/YYYY)
 * Hỗ trợ cả năm dương lịch và năm tài chính
 * @param year - Năm để tính quý (mặc định là năm hiện tại)
 * @param isNamTaiChinh - Có phải năm tài chính không (mặc định false)
 * @param monthTaiChinh - Tháng bắt đầu của năm tài chính (mặc định 0)
 * @returns Chuỗi dateRange dạng "DD/MM/YYYY - DD/MM/YYYY"
 */
export const getCurrentQuarterRangeString = (
  year?: number,
  isNamTaiChinh?: boolean,
  monthTaiChinh?: number
): string => {
  const today = new Date();
  const targetYear = year ?? today.getFullYear();
  const currentMonth = today.getMonth() + 1; // 1-12

  // Sử dụng tham số trực tiếp
  const isNamTaiChinhValue = isNamTaiChinh ?? false;
  const monthTaiChinhValue = monthTaiChinh ?? 0;

  // Tính months để xác định quý
  const monthsLabels = datePickerLabels.vi.months;
  const months = calculateMonths(targetYear, isNamTaiChinhValue, monthTaiChinhValue, monthsLabels);

  // Tìm tháng hiện tại và quý của nó
  const currentMonthIndex = findCurrentMonthIndex(currentMonth, isNamTaiChinhValue, monthTaiChinhValue);
  if (currentMonthIndex === -1 || currentMonthIndex >= months.length) {
    // Fallback về logic cũ nếu không tìm thấy
    const currentQuarter = Math.ceil(currentMonth / 3);
    const startMonth = (currentQuarter - 1) * 3 + 1;
    const endMonth = currentQuarter * 3;
    const endDate = new Date(targetYear, endMonth, 0);
    const formatDate = (day: number, month: number, yearValue: number): string => {
      const dayStr = day.toString().padStart(2, '0');
      const monthStr = month.toString().padStart(2, '0');
      return `${dayStr}/${monthStr}/${yearValue}`;
    };
    return `${formatDate(1, startMonth, targetYear)} - ${formatDate(endDate.getDate(), endMonth, targetYear)}`;
  }

  const currentMonthData = months[currentMonthIndex];
  const currentQuarterIndex = currentMonthData.quarterIndex;

  // Tìm tất cả tháng trong quý đó
  const quarterMonths = months.filter((m) => m.quarterIndex === currentQuarterIndex);
  if (quarterMonths.length === 0) {
    // Fallback
    const currentQuarter = Math.ceil(currentMonth / 3);
    const startMonth = (currentQuarter - 1) * 3 + 1;
    const endMonth = currentQuarter * 3;
    const endDate = new Date(targetYear, endMonth, 0);
    const formatDate = (day: number, month: number, yearValue: number): string => {
      const dayStr = day.toString().padStart(2, '0');
      const monthStr = month.toString().padStart(2, '0');
      return `${dayStr}/${monthStr}/${yearValue}`;
    };
    return `${formatDate(1, startMonth, targetYear)} - ${formatDate(endDate.getDate(), endMonth, targetYear)}`;
  }

  // Lấy tháng đầu và cuối của quý
  const startMonthData = quarterMonths[0];
  const endMonthData = quarterMonths[quarterMonths.length - 1];

  // Tính ngày bắt đầu (ngày 1 của tháng đầu quý)
  const startDate = new Date(startMonthData.actualYear, startMonthData.actualMonth - 1, 1);
  // Tính ngày cuối (ngày cuối cùng của tháng cuối quý)
  const endDate = new Date(endMonthData.actualYear, endMonthData.actualMonth, 0);

  // Format dateRange: DD/MM/YYYY - DD/MM/YYYY
  const formatDate = (date: Date): string => {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const yearValue = date.getFullYear();
    return `${day}/${month}/${yearValue}`;
  };

  return `${formatDate(startDate)} - ${formatDate(endDate)}`;
};

/**
 * Helper: Tính startIndex và endIndex cho quý hiện tại khi chọn năm hiện tại
 * @param year - Năm được chọn
 * @param yearData - Dữ liệu giai đoạn/năm (optional)
 * @param defaultNamTaiChinh - Giá trị mặc định cho năm tài chính (optional)
 * @param defaultMonthTaiChinh - Giá trị mặc định cho tháng tài chính (optional)
 * @param monthsLabels - Mảng label các tháng (optional, mặc định là ['Thg 01', ..., 'Thg 12'])
 * @returns { startIndex, endIndex } nếu là năm hiện tại, null nếu không phải
 */
export const getCurrentQuarterIndices = (
  year: number,
  yearData?: YearStage[],
  monthsLabels: string[] = datePickerLabels.vi.months
): { startIndex: number; endIndex: number } | null => {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1; // 1-12

  // Nếu không phải năm hiện tại, trả về null (caller sẽ xử lý full year)
  if (Number(year) !== currentYear) {
    return null;
  }

  // Lấy thông tin năm tài chính từ yearData dựa trên year
  const { isNamTaiChinh, monthTaiChinh } = getFiscalYearInfo(year, yearData);

  // Tìm tháng hiện tại trong mảng months
  const currentMonthIndex = findCurrentMonthIndex(currentMonth, isNamTaiChinh, monthTaiChinh);
  if (currentMonthIndex === -1) {
    return null;
  }

  // Tính months để lấy quarterIndex
  const months = calculateMonths(year, isNamTaiChinh, monthTaiChinh, monthsLabels);

  // Tìm quý của tháng hiện tại
  const currentMonthData = months[currentMonthIndex];
  if (!currentMonthData) {
    return null;
  }

  const currentQuarterIndex = currentMonthData.quarterIndex;
  // Tìm tất cả tháng trong quý đó
  const quarterMonths = months.filter((m) => m.quarterIndex === currentQuarterIndex);
  if (quarterMonths.length === 0) {
    return null;
  }

  const start = quarterMonths[0].index;
  const end = quarterMonths[quarterMonths.length - 1].index;
  return { startIndex: start, endIndex: end };
};
